import { realtimeService } from '@peritasai/data-access';
import {
  AssetEntity,
  assetRepository,
  caseScheduleRepository,
} from '@peritasai/repository';
import {
  AssetLocationUpdatedEvent,
  AssetStatusUpdatedEvent,
  AssetType,
  AssetUpdateEventFilters,
  CaseBuildStatus,
  RealtimeEventType,
  type EventSubscription,
} from '@peritasai/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DetectedAsset } from '../types/build-cart';
import { getCartActionLabel } from '../utils/cartAction';

const RECENT_DETECTION_WINDOW_MS = 5 * 60 * 1000;
const RESERVE_SUCCESS_DISPLAY_MS = 1200;
const DETECTABLE_ASSET_TYPES = [AssetType.TRAY, AssetType.CASE_CART];

export type UseCaseAssignmentDiscoveryOptions = {
  /** True while the hub should be actively scanning for this case. */
  enabled: boolean;
  /** True when the flow is on its success view (preserves the success timeout on cleanup). */
  isSuccess: boolean;
  locationId: string | null;
  caseId: string | null;
  excludedAssetIds: readonly string[];
  onHubOffline: () => void;
  onReserveSuccess: () => void;
  onReserveSettled: () => void;
  onCancel: () => void;
};

export type UseCaseAssignmentDiscoveryResult = {
  detectedAssets: DetectedAsset[];
  handleToggleAsset: (assetId: string) => void;
  handleRemoveDetectedAsset: (assetId: string) => void;
  handleReserve: (trayAssetIds: string[], cartAssetId?: string) => void;
  handleCancel: () => void;
  resetDiscoverySession: () => void;
};

type ResetOptions = {
  preserveReserveSuccessTimeout?: boolean;
};

export const useCaseAssignmentDiscovery = ({
  enabled,
  isSuccess,
  locationId,
  caseId,
  excludedAssetIds,
  onHubOffline,
  onReserveSuccess,
  onReserveSettled,
  onCancel,
}: UseCaseAssignmentDiscoveryOptions): UseCaseAssignmentDiscoveryResult => {
  const [detectedAssets, setDetectedAssets] = useState<DetectedAsset[]>([]);
  // Keep the latest callbacks in a ref so the scan/stream effect below doesn't
  // re-run (and reconnect the stream) when a caller passes inline callbacks.
  const callbacksRef = useRef({
    onHubOffline,
    onReserveSuccess,
    onReserveSettled,
    onCancel,
  });
  callbacksRef.current = {
    onHubOffline,
    onReserveSuccess,
    onReserveSettled,
    onCancel,
  };
  const streamAbortRef = useRef<AbortController | null>(null);
  const startScanRequestInFlightRef = useRef(false);
  const streamErrorCountRef = useRef(0);
  const isReservingRef = useRef(false);
  const reserveSuccessTimeoutRef = useRef<number | null>(null);

  const clearActiveStream = useCallback(() => {
    if (streamAbortRef.current) {
      streamAbortRef.current.abort();
      streamAbortRef.current = null;
    }
  }, []);

  const clearReserveSuccessTimeout = useCallback(() => {
    if (reserveSuccessTimeoutRef.current && typeof window !== 'undefined') {
      window.clearTimeout(reserveSuccessTimeoutRef.current);
      reserveSuccessTimeoutRef.current = null;
    }
  }, []);

  const clearDetectedAssets = useCallback(() => {
    setDetectedAssets([]);
  }, []);

  const resetDiscoverySession = useCallback(
    ({ preserveReserveSuccessTimeout = false }: ResetOptions = {}) => {
      if (!preserveReserveSuccessTimeout) {
        clearReserveSuccessTimeout();
      }
      clearActiveStream();
      clearDetectedAssets();
      startScanRequestInFlightRef.current = false;
      streamErrorCountRef.current = 0;
      isReservingRef.current = false;
    },
    [clearActiveStream, clearReserveSuccessTimeout, clearDetectedAssets]
  );

  const handleCancel = useCallback(() => {
    resetDiscoverySession();
    callbacksRef.current.onCancel();
  }, [resetDiscoverySession]);

  const handleToggleAsset = useCallback((assetId: string) => {
    setDetectedAssets((prev) =>
      prev.map((asset) =>
        asset.id === assetId ? { ...asset, selected: !asset.selected } : asset
      )
    );
  }, []);

  // Unlike handleToggleAsset (which only deselects), this drops the entry
  // entirely so a future re-detection isn't stuck inheriting selected: false.
  const handleRemoveDetectedAsset = useCallback((assetId: string) => {
    setDetectedAssets((prev) => prev.filter((asset) => asset.id !== assetId));
  }, []);

  const visibleDetectedAssets = useMemo(
    () =>
      detectedAssets.filter((asset) => !excludedAssetIds.includes(asset.id)),
    [detectedAssets, excludedAssetIds]
  );

  // Takes the tray/cart ids to reserve explicitly — the caller
  // (useCaseCartDetailViewModel) computes them from every "On Cart" tray,
  // not just this session's live-detected selection.
  const handleReserve = useCallback(
    (trayAssetIds: string[], cartAssetId?: string) => {
      if (isReservingRef.current || !caseId) return;
      // Backend requires at least one tray in the batch — a cart alone cannot be reserved.
      if (trayAssetIds.length === 0) return;

      isReservingRef.current = true;

      void caseScheduleRepository
        .reserveTrackedItemsBatch(caseId, trayAssetIds, cartAssetId)
        .then(async (updatedCase) => {
          // Keep buildStatus in sync with actual tray coverage, same as
          // build-cart.tsx's dispatch-time recalculation.
          const checklist = updatedCase.buildChecklist;
          if (checklist) {
            const isFullyCovered =
              getCartActionLabel(
                checklist.traysAllocated,
                checklist.traysReserved,
                checklist.traysTotal
              ) === 'dispatch';
            if (isFullyCovered) {
              try {
                await caseScheduleRepository.updateBuildStatus(
                  caseId,
                  CaseBuildStatus.COMPLETE
                );
              } catch (error) {
                // Don't let a failure here mask the reserve that already succeeded.
                console.error(
                  'Failed to sync buildStatus after reserve',
                  error
                );
              }
            }
          }

          callbacksRef.current.onReserveSuccess();
          if (typeof window !== 'undefined') {
            reserveSuccessTimeoutRef.current = window.setTimeout(() => {
              reserveSuccessTimeoutRef.current = null;
              resetDiscoverySession();
              callbacksRef.current.onReserveSettled();
            }, RESERVE_SUCCESS_DISPLAY_MS);
          } else {
            resetDiscoverySession();
            callbacksRef.current.onReserveSettled();
          }
        })
        .catch((error: unknown) => {
          console.error('Failed to reserve trays to case', error);
        })
        .finally(() => {
          isReservingRef.current = false;
        });
    },
    [caseId, resetDiscoverySession]
  );

  useEffect(() => {
    let isActive = true;

    if (!locationId) {
      resetDiscoverySession();
      return;
    }

    if (!enabled) {
      resetDiscoverySession({ preserveReserveSuccessTimeout: isSuccess });
      return;
    }

    const startScan = () => {
      if (startScanRequestInFlightRef.current) return;
      startScanRequestInFlightRef.current = true;

      void assetRepository
        .getAssets({
          type: DETECTABLE_ASSET_TYPES.join(','),
          locationId,
          pageSize: 100,
        })
        .then((result) => {
          if (!isActive) return;
          const now = Date.now();
          setDetectedAssets((prev) =>
            result
              .filter((item) => {
                if (!item.locationUpdatedAt) return false;
                const diff = Math.abs(now - item.locationUpdatedAt.getTime());
                return diff <= RECENT_DETECTION_WINDOW_MS;
              })
              .map((asset) => ({
                id: asset.id,
                name: asset.name,
                subtitle: asset.referenceNumber,
                assetType: asset.type,
                catalogItemId: asset.assetCatalogId,
                locationDisplay: asset.getLocationDisplay(),
                status: asset.status,
                selected:
                  prev.find((item) => item.id === asset.id)?.selected ?? true,
              }))
          );
          startScanRequestInFlightRef.current = false;
        })
        .catch(() => {
          if (!isActive) return;
          startScanRequestInFlightRef.current = false;
          callbacksRef.current.onHubOffline();
        });
    };

    if (streamAbortRef.current) return;

    // Unfiltered by location: the server's locationId filter matches the new
    // location only, so it would hide detected assets leaving the station.
    const filter: AssetUpdateEventFilters = {
      type: DETECTABLE_ASSET_TYPES.join(','),
    };
    const subscriptions: EventSubscription[] = [
      {
        eventType: RealtimeEventType.ASSET_LOCATION_UPDATED,
        filters: filter,
      },
      {
        eventType: RealtimeEventType.ASSET_STATUS_UPDATED,
        filters: filter,
      },
    ];

    const controller = new AbortController();
    streamAbortRef.current = controller;
    streamErrorCountRef.current = 0;

    void realtimeService.connect({
      subscriptions,
      signal: controller.signal,
      onEvent(event) {
        if (
          !event ||
          (event.type !== RealtimeEventType.ASSET_LOCATION_UPDATED &&
            event.type !== RealtimeEventType.ASSET_STATUS_UPDATED)
        ) {
          return;
        }
        streamErrorCountRef.current = 0;

        const assetData = (
          event as AssetLocationUpdatedEvent | AssetStatusUpdatedEvent
        ).data.asset;
        if (!DETECTABLE_ASSET_TYPES.includes(assetData.type)) return;

        if (assetData.location?.id !== locationId) {
          // A previously-detected asset moved away from the station — drop
          // it so its tray falls back to pending instead of staying stale.
          setDetectedAssets((prev) =>
            prev.some((item) => item.id === assetData.id)
              ? prev.filter((item) => item.id !== assetData.id)
              : prev
          );
          return;
        }

        if (event.type === RealtimeEventType.ASSET_STATUS_UPDATED) {
          // Only patches an already-detected tray's status — never inserts,
          // so a status-only event can't promote a tray from pending to on-cart.
          setDetectedAssets((prev) => {
            if (!prev.some((item) => item.id === assetData.id)) return prev;
            const detectedEntity = new AssetEntity(assetData);
            return prev.map((item) =>
              item.id === assetData.id
                ? { ...item, status: detectedEntity.status }
                : item
            );
          });
          return;
        }

        setDetectedAssets((prev) => {
          const detectedEntity = new AssetEntity(assetData);
          const newAsset: DetectedAsset = {
            id: assetData.id,
            name: assetData.name ?? 'Detected asset',
            subtitle: assetData.referenceNumber ?? assetData.id,
            assetType: assetData.type,
            catalogItemId: assetData.assetCatalogId,
            locationDisplay: detectedEntity.getLocationDisplay(),
            status: detectedEntity.status,
            selected:
              prev.find((item) => item.id === assetData.id)?.selected ?? true,
          };
          return [...prev.filter((item) => item.id !== newAsset.id), newAsset];
        });
      },
      onStateChange(state) {
        if (controller.signal.aborted) return;
        if (state.connected) {
          streamErrorCountRef.current = 0;
          startScan();
          return;
        }
        streamAbortRef.current = null;

        if (!state.error) {
          callbacksRef.current.onHubOffline();
          return;
        }

        const errorMessage = state.error.message ?? '';
        const isAbortError =
          state.error.name === 'AbortError' || /abort/i.test(errorMessage);
        const is401 = /401|Unauthorized/i.test(errorMessage);
        if (isAbortError || is401) return;

        const isUnexpectedClose = /stream closed unexpectedly/i.test(
          errorMessage
        );
        if (isUnexpectedClose) {
          callbacksRef.current.onHubOffline();
          return;
        }

        streamErrorCountRef.current += 1;
        if (streamErrorCountRef.current < 3) return;

        console.error('Case assignment discovery SSE error', state.error);
        callbacksRef.current.onHubOffline();
      },
    });

    return () => {
      clearActiveStream();
      isActive = false;
    };
  }, [
    clearActiveStream,
    enabled,
    isSuccess,
    locationId,
    resetDiscoverySession,
  ]);

  useEffect(() => {
    return () => {
      resetDiscoverySession();
    };
  }, [resetDiscoverySession]);

  return {
    detectedAssets: visibleDetectedAssets,
    handleToggleAsset,
    handleRemoveDetectedAsset,
    handleReserve,
    handleCancel,
    resetDiscoverySession,
  };
};
